import { useMemo, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

// A filter that is a plain <select> until the list gets long, at which point
// scrolling a few hundred engineers to find one name stops being usable. Type
// to narrow, click to pick, X to clear.
//
// Options are either plain strings, or { value, label } where the two differ —
// the engineer filter matches on the email address the job records carry, but
// nobody searches for "phillip.h@nanois.co.uk", so it shows the name and
// searches both.
export default function SearchableSelect({
  value, onChange, options, allLabel = 'All', placeholder = 'Type to search…', className = '', id,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  const items = useMemo(() => (options || []).map((o) => (
    typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: o.label || o.value }
  )), [options]);

  const selected = items.find((o) => o.value === value);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = needle
      ? items.filter((o) => `${o.label} ${o.value}`.toLowerCase().includes(needle))
      : items;
    // A long list is slow to render and nobody reads past the first screenful;
    // if what they want isn't here, another keystroke gets them there.
    return list.slice(0, 50);
  }, [items, query]);

  const pick = (next) => {
    onChange(next);
    setOpen(false);
    setQuery('');
    if (boxRef.current) boxRef.current.blur();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); boxRef.current?.blur(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, shown.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter' && open) { e.preventDefault(); if (shown[active]) pick(shown[active].value); }
  };

  const base = 'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm '
    + 'focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

  return (
    <div className={`relative ${className}`}>
      <input
        id={id}
        ref={boxRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        // Closed, it reads as the current selection; focused, it becomes a
        // search box, so one control does both jobs.
        value={open ? query : (selected?.label || value || '')}
        placeholder={value ? '' : allLabel}
        onChange={(e) => { setQuery(e.target.value); setActive(0); setOpen(true); }}
        onFocus={() => { setQuery(''); setActive(0); setOpen(true); }}
        // Deferred so a click on an option registers before the list unmounts.
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        className={`${base} w-full ${value && !open ? 'pr-7' : ''}`}
        title={open ? placeholder : (selected?.label || value || allLabel)}
      />

      {value && !open ? (
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); pick(''); }}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-700"
          aria-label={`Clear — show ${allLabel.toLowerCase()}`}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      ) : null}

      {open ? (
        <ul className="absolute z-20 mt-1 max-h-64 w-full min-w-[12rem] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          <li>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(''); }}
              className="block w-full px-3 py-1.5 text-left text-sm text-gray-500 hover:bg-brand-light"
            >
              {allLabel}
            </button>
          </li>
          {shown.map((o, i) => (
            <li key={o.value}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(o.value); }}
                onMouseEnter={() => setActive(i)}
                className={`block w-full truncate px-3 py-1.5 text-left text-sm ${
                  i === active ? 'bg-brand-light text-brand-dark' : 'text-gray-700 hover:bg-brand-light'}`}
                title={o.label === o.value ? o.label : `${o.label} — ${o.value}`}
              >
                {o.label}
              </button>
            </li>
          ))}
          {!shown.length ? (
            <li className="px-3 py-2 text-sm text-gray-400">No match</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
